import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { AiCommerceChoice } from '../services/ai.types';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';

/**
 * The options sent with a message — an AI suggestion's choices or ones written by hand: rename
 * each option (WhatsApp shows 20 characters on a button, 24 on a list row), edit its
 * description, reorder, remove, or add new ones. Up to 3 options are sent as buttons, more (or
 * any with a description) as a list. An AI option keeps its id, so a tap still means that
 * product / size / cart action; added options are plain answers (disai:o:…).
 */
export const MAX_CHOICES = 10;

export const newChoice = (): AiCommerceChoice => ({ id: `disai:o:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, label: '' });

export function ChoiceDraftEditor({ choices, onChange }: { choices: AiCommerceChoice[]; onChange: (next: AiCommerceChoice[]) => void }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { width, height } = useWindowDimensions();
  const [collapsed, setCollapsed] = useState(false);
  // Never taller than about a third of the screen, so the composer stays in view; the
  // options scroll inside. Wide screens put the description beside the option.
  const maxListHeight = Math.max(120, Math.min(300, height * 0.32));
  const wide = width >= 640;
  const set = (index: number, patch: Partial<AiCommerceChoice>) => onChange(choices.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= choices.length) return;
    const next = [...choices];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const asList = choices.length > 3 || choices.some((c) => c.description?.trim());
  const max = asList ? 24 : 20;
  const tooLong = choices.filter((c) => c.label.length > max).length;
  return (
    <View style={styles.box}>
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <TouchableOpacity style={[styles.headerToggle, { flexDirection: rtl.rowDirection }]} onPress={() => setCollapsed((c) => !c)} accessibilityRole="button">
          <Ionicons name={collapsed ? 'chevron-down' : 'chevron-up'} size={14} color={colors.text.secondary as string} />
          <Text style={styles.title} numberOfLines={1}>{t('ai.choices.title')} ({choices.length})</Text>
          <Text style={styles.hint} numberOfLines={1}>· {asList ? t('ai.choices.asList') : t('ai.choices.asButtons')}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => onChange([])} accessibilityRole="button" accessibilityLabel={t('ai.quick.removeChoices')} style={styles.icon}>
          <Ionicons name="close" size={16} color={colors.text.secondary as string} />
        </TouchableOpacity>
      </View>
      {!collapsed && !!tooLong && <Text style={[styles.warn, { textAlign: rtl.textStart }]}>{t('ai.choices.tooLong', { max })}</Text>}
      {!collapsed && (
        <ScrollView style={{ maxHeight: maxListHeight }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
          {choices.map((choice, index) => {
            const long = choice.label.length > max;
            return (
              <View key={choice.id} style={[styles.row, { flexDirection: wide ? rtl.rowDirection : 'column' }]}>
                <View style={[styles.rowTop, { flexDirection: rtl.rowDirection }, wide && { flex: 3 }]}>
                  <TextInput
                    style={[styles.input, { textAlign: rtl.textStart }, long && styles.inputLong]}
                    value={choice.label}
                    onChangeText={(text) => set(index, { label: text.slice(0, 60) })}
                    placeholder={t('ai.choices.optionPlaceholder', { n: index + 1 })}
                    placeholderTextColor={colors.text.secondary as string}
                    accessibilityLabel={t('ai.choices.optionPlaceholder', { n: index + 1 })}
                  />
                  <Text style={[styles.count, long && { color: colors.warning as string }]}>{choice.label.length}/{max}</Text>
                </View>
                <View style={[styles.rowTop, { flexDirection: rtl.rowDirection }, wide && { flex: 2 }]}>
                  <TextInput
                    style={[styles.input, styles.description, { textAlign: rtl.textStart }]}
                    value={choice.description || ''}
                    onChangeText={(text) => set(index, { description: text.slice(0, 72) || undefined })}
                    placeholder={t('ai.choices.descriptionPlaceholder')}
                    placeholderTextColor={colors.text.secondary as string}
                  />
                  <TouchableOpacity accessibilityLabel={t('ai.choices.moveUp')} disabled={index === 0} onPress={() => move(index, -1)} style={styles.icon}>
                    <Ionicons name="chevron-up" size={16} color={index === 0 ? (colors.border as string) : (colors.text.secondary as string)} />
                  </TouchableOpacity>
                  <TouchableOpacity accessibilityLabel={t('ai.choices.moveDown')} disabled={index === choices.length - 1} onPress={() => move(index, 1)} style={styles.icon}>
                    <Ionicons name="chevron-down" size={16} color={index === choices.length - 1 ? (colors.border as string) : (colors.text.secondary as string)} />
                  </TouchableOpacity>
                  <TouchableOpacity accessibilityLabel={t('ai.choices.remove')} onPress={() => onChange(choices.filter((_, i) => i !== index))} style={styles.icon}>
                    <Ionicons name="close-circle" size={18} color={colors.error as string} />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
          {choices.length < MAX_CHOICES && (
            <TouchableOpacity style={[styles.add, { flexDirection: rtl.rowDirection }]} onPress={() => onChange([...choices, newChoice()])} accessibilityRole="button">
              <Ionicons name="add-circle-outline" size={16} color={colors.primary as string} />
              <Text style={styles.addText}>{t('ai.choices.add')}</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      )}
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  box: { marginHorizontal: spacing.sm, marginTop: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: 6, gap: 4, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  header: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.xs },
  headerToggle: { flex: 1, alignItems: 'center', gap: 4, minWidth: 0 },
  title: { flexShrink: 1, fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  hint: { flexShrink: 1, fontSize: typography.fontSize.xs, color: colors.text.secondary },
  row: { gap: 4, paddingVertical: 4, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  rowTop: { alignItems: 'center', gap: 4 },
  input: { flex: 1, minWidth: 0, height: 30, paddingHorizontal: spacing.xs, paddingVertical: 0, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, color: colors.text.primary, fontSize: typography.fontSize.xs, backgroundColor: colors.background.base },
  inputLong: { borderColor: colors.warning },
  description: { color: colors.text.secondary },
  count: { fontSize: 10, color: colors.text.secondary, minWidth: 34, textAlign: 'center' },
  icon: { padding: 3 },
  warn: { fontSize: 10, color: colors.warning },
  add: { alignItems: 'center', gap: 4, paddingVertical: 6 },
  addText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary },
});
